import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import TaskFormDialog from "@/components/dialogs/TaskFormDialog";
import MeetingFormDialog from "@/components/dialogs/MeetingFormDialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Plus, ListChecks, CalendarClock, LogOut, ChevronDown } from "lucide-react";

export default function Header({ title }) {
  const { user, logout } = useAuth();
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);

  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <header
        data-testid="app-header"
        className="h-16 sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0] flex items-center justify-between px-8"
      >
        <h1 data-testid="page-title" className="font-heading text-lg font-semibold text-[#1C1C1E] tracking-tight">
          {title}
        </h1>
        <div className="flex items-center gap-3">
          {user?.role === "admin" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="quick-action-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white gap-1.5">
                  <Plus className="h-4 w-4" /> Quick Action <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="quick-action-new-task" onClick={() => setTaskDialogOpen(true)}>
                  <ListChecks className="h-4 w-4 mr-2" /> Allocate New Task
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="quick-action-log-meeting" onClick={() => setMeetingDialogOpen(true)}>
                  <CalendarClock className="h-4 w-4 mr-2" /> Log Client Meeting
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-md hover:bg-[#F8F9FA] transition-colors duration-200">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-[#00823B] text-white text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <div className="px-3 py-2 text-xs text-[#4B5563]">
                <p className="font-medium text-[#1C1C1E]">{user?.name}</p>
                <p className="truncate">{user?.email}</p>
              </div>
              <DropdownMenuItem data-testid="logout-menu-item" onClick={logout} className="text-red-600">
                <LogOut className="h-4 w-4 mr-2" /> Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <TaskFormDialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen} />
      <MeetingFormDialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen} />
    </>
  );
}
